'use client'

import type { ChainId } from '@morpho-org/blue-sdk'

import type { VaultV2Risk } from '@/lib/procedures/vaultV2Risk.contract'
import type { VaultV2Roles } from '@/lib/procedures/vaultV2Roles.contract'

import { getSafeQuorum, hasAragonMetadata } from '@/helpers/quorum'

import AddressWithCopyAndExplorer from '../../components/AddressWithCopyAndExplorer'
import { VaultV2RowList, VaultV2RowListRow } from '../../components/VaultV2RowList'

interface VaultRolesClientProps {
  chainId: ChainId
  owner: VaultV2Risk['owner']
  curator: VaultV2Risk['curator']
  sentinels: VaultV2Roles['sentinels']
}

const VaultRolesClient = ({ chainId, owner, curator, sentinels }: VaultRolesClientProps) => {
  if (!owner || !curator) {
    return null
  }

  const ownerSafeQuorum = getSafeQuorum(owner.metadata)

  const curatorSafeQuorum = getSafeQuorum(curator.metadata)

  const sentinelsMetadata = sentinels
    ? sentinels.map(sentinel => ({
        ...sentinel,
        safeQuorum: getSafeQuorum(sentinel.metadata),
        isAragonDAO: hasAragonMetadata(sentinel.metadata)
      }))
    : []

  const [firstSentinel, ...restSentinels] = sentinelsMetadata

  return (
    <VaultV2RowList>
      <VaultV2RowListRow
        label="Owner"
        tooltip="Assigns and changes the key roles that govern the vault. Has no direct control over funds or risk parameters. Only one address can hold this role."
      >
        <AddressWithCopyAndExplorer
          address={owner.address}
          chainId={chainId}
          safeQuorum={ownerSafeQuorum}
          isAragonDAO={hasAragonMetadata(owner.metadata)}
        />
      </VaultV2RowListRow>
      <VaultV2RowListRow
        label="Curator"
        tooltip="Configures risk parameters and allocation rules of the vault. Only one address can hold this role."
      >
        <AddressWithCopyAndExplorer
          address={curator.address}
          chainId={chainId}
          safeQuorum={curatorSafeQuorum}
          isAragonDAO={hasAragonMetadata(curator.metadata)}
        />
      </VaultV2RowListRow>
      <VaultV2RowListRow
        label="Sentinel 1"
        tooltip="Can trigger predefined protective functions such as pausing or restricting certain actions. Acts as a check on the Curator. Multiple addresses can hold this role."
      >
        <AddressWithCopyAndExplorer
          // If we have no sentinel, we still want to display a placeholder to show it's a possible role
          address={firstSentinel?.address ?? '0x0000000000000000000000000000000000000000'}
          chainId={chainId}
          safeQuorum={firstSentinel?.safeQuorum}
          isAragonDAO={firstSentinel?.isAragonDAO}
        />
      </VaultV2RowListRow>
      {restSentinels.map((sentinel, index) => (
        <VaultV2RowListRow key={index} label={`Sentinel ${index + 2}`}>
          <AddressWithCopyAndExplorer
            address={sentinel.address}
            chainId={chainId}
            safeQuorum={sentinel.safeQuorum}
            isAragonDAO={sentinel.isAragonDAO}
          />
        </VaultV2RowListRow>
      ))}
    </VaultV2RowList>
  )
}

export default VaultRolesClient
